import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

/**
 * フローティングデバッグトグルボタン
 * 開発環境でのみ表示され、デバッグ情報の表示/非表示を切り替えます
 */
const DebugToggle = ({ onToggle, position = 'top-right' }) => {
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // localStorageからの初期値読み込み（安全に）
  useEffect(() => {
    // 開発環境以外では何もしない
    if (process.env.NODE_ENV !== 'development') {
      return;
    }

    try {
      const savedDebugMode = localStorage.getItem('quiz-debug-mode');
      const initialDebugMode = savedDebugMode !== null ? savedDebugMode === 'true' : true;
      setIsDebugMode(initialDebugMode);
      setIsLoaded(true);
      
      // 親コンポーネントに初期状態を通知
      if (onToggle) {
        onToggle(initialDebugMode);
      }
    } catch (error) {
      console.warn('DebugToggle: localStorageの読み込みに失敗しました', error);
      setIsDebugMode(true); // デフォルトは表示
      setIsLoaded(true);
      if (onToggle) {
        onToggle(true);
      }
    }
  }, [onToggle]);

  // 開発環境以外では何も表示しない
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  // デバッグモードの切り替え処理
  const handleToggle = () => {
    const newDebugMode = !isDebugMode;
    setIsDebugMode(newDebugMode);
    
    // localStorageに保存（安全に）
    try {
      localStorage.setItem('quiz-debug-mode', newDebugMode.toString());
    } catch (error) {
      console.warn('DebugToggle: localStorageの保存に失敗しました', error);
    }
    
    // 親コンポーネントに変更を通知
    if (onToggle) {
      onToggle(newDebugMode);
    }
  };

  // 位置に応じたCSSクラス
  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'top-right':
        return 'top-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      default:
        return 'top-4 right-4';
    }
  };

  // ロード完了まで何も表示しない（チラつき防止）
  if (!isLoaded) {
    return null;
  }

  return (
    <button
      onClick={handleToggle}
      className={`fixed ${getPositionClasses()} z-50 
        ${isDebugMode 
          ? 'bg-yellow-400 hover:bg-yellow-500 text-black' 
          : 'bg-gray-400 hover:bg-gray-500 text-white'
        } 
        px-3 py-1 rounded-full text-xs font-medium shadow-lg 
        transition-all duration-200 transform hover:scale-105
        select-none cursor-pointer`}
      title={`デバッグ情報を${isDebugMode ? '非表示' : '表示'}にする`}
      aria-label={`デバッグ情報切り替えボタン - 現在${isDebugMode ? 'ON' : 'OFF'}`}
    >
      🔧 Debug: {isDebugMode ? 'ON' : 'OFF'}
    </button>
  );
};

DebugToggle.propTypes = {
  onToggle: PropTypes.func.isRequired,
  position: PropTypes.oneOf(['top-left', 'top-right', 'bottom-left', 'bottom-right']),
};

export default DebugToggle; 